import { useState, useEffect } from 'react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { TrendingUp, DollarSign, Package, BarChart3, Receipt, Percent, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function downloadCSV(rows, gstRate) {
  const header = ['Month', 'Orders', `Base Revenue (excl. GST)`, `GST @ ${gstRate}%`, 'Total Revenue (incl. GST)'];
  const total = rows.reduce((a, r) => ({
    order_count: a.order_count + r.order_count,
    base_revenue: a.base_revenue + r.base_revenue,
    gst_collected: a.gst_collected + r.gst_collected,
    total_revenue: a.total_revenue + r.total_revenue,
  }), { order_count: 0, base_revenue: 0, gst_collected: 0, total_revenue: 0 });

  const csvRows = [
    header,
    ...rows.map(r => [r.month, r.order_count, r.base_revenue.toFixed(2), r.gst_collected.toFixed(2), r.total_revenue.toFixed(2)]),
    [],
    ['TOTAL', total.order_count, total.base_revenue.toFixed(2), total.gst_collected.toFixed(2), total.total_revenue.toFixed(2)],
  ];

  const csv = csvRows.map(row => row.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `gst_report_${new Date().toISOString().slice(0,7)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function RevenueTracking() {
  const [revenueData, setRevenueData] = useState(null);
  const [gstReport, setGstReport] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchRevenueData(); }, []);

  const fetchRevenueData = async () => {
    try {
      const response = await axios.get(`${API}/admin/revenue/overview`);
      setRevenueData(response.data);
    } catch (error) {
      toast.error('Failed to fetch revenue data');
    } finally {
      setLoading(false);
    }
  };

  const handleExportGST = async () => {
    setExporting(true);
    try {
      const { data } = await axios.get(`${API}/admin/revenue/gst-report`);
      if (!data.rows?.length) { toast.info('No provisioned orders found for export'); return; }
      downloadCSV(data.rows, data.gst_rate_pct);
      setGstReport(data);
      toast.success('GST Report downloaded!');
    } catch {
      toast.error('Failed to generate GST report');
    } finally {
      setExporting(false);
    }
  };

  const stats = revenueData ? [
    {
      title: 'Total Revenue (incl. GST)',
      value: fmt(revenueData.total_revenue),
      icon: DollarSign,
      color: 'text-green-600',
      bg: 'bg-green-50',
      testid: 'stat-total-revenue'
    },
    {
      title: 'Base Revenue (excl. GST)',
      value: fmt(revenueData.total_revenue_excl_gst),
      icon: Receipt,
      color: 'text-indigo-600',
      bg: 'bg-indigo-50',
      testid: 'stat-base-revenue'
    },
    {
      title: 'GST Collected',
      value: fmt(revenueData.total_gst_collected),
      icon: Percent,
      color: 'text-amber-600',
      bg: 'bg-amber-50',
      testid: 'stat-gst-collected'
    },
    {
      title: 'Total Margin',
      value: fmt(revenueData.total_margin),
      icon: TrendingUp,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
      testid: 'stat-total-margin'
    },
    {
      title: 'Margin %',
      value: `${revenueData.margin_percentage.toFixed(2)}%`,
      icon: BarChart3,
      color: 'text-purple-600',
      bg: 'bg-purple-50',
      testid: 'stat-margin-pct'
    },
    {
      title: 'Total Orders',
      value: revenueData.total_orders,
      icon: Package,
      color: 'text-orange-600',
      bg: 'bg-orange-50',
      testid: 'stat-total-orders'
    }
  ] : [];

  return (
    <DashboardLayout role="admin">
      <div className="space-y-6">
        <div className="mb-2 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-4xl font-bold tracking-tight" data-testid="revenue-page-title">Revenue Tracking</h1>
            <p className="text-slate-500 mt-2">Monitor sales, commissions, and financial metrics</p>
          </div>
          <Button
            onClick={handleExportGST}
            disabled={exporting}
            data-testid="export-gst-btn"
            className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white shrink-0"
          >
            <Download className="h-4 w-4" />
            {exporting ? 'Generating…' : 'Export GST Report'}
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 min-[480px]:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 md:gap-4 mb-5">
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <Card key={index} className="hover:shadow-lg transition-shadow" data-testid={stat.testid || `revenue-stat-${index}`}>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-xs font-medium text-slate-600 leading-tight">
                        {stat.title}
                      </CardTitle>
                      <div className={`${stat.bg} p-2 rounded-md shrink-0`}>
                        <Icon className={`h-4 w-4 ${stat.color}`} />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="text-xl font-bold" data-testid={`revenue-value-${index}`}>{stat.value}</div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <Card data-testid="reseller-revenue-table">
              <CardHeader>
                <CardTitle>Revenue by Reseller</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {revenueData.resellers.length === 0 ? (
                  <div className="text-center py-16">
                    <p className="text-slate-500">No sales data yet</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-nowrap">Reseller</TableHead>
                        <TableHead className="text-nowrap">Orders</TableHead>
                        <TableHead className="text-nowrap">Total Sales</TableHead>
                        <TableHead className="text-nowrap">Base Cost</TableHead>
                        <TableHead className="text-nowrap">Margin</TableHead>
                        <TableHead className="text-nowrap">Margin %</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {revenueData.resellers.map((reseller) => {
                        const marginPercentage = reseller.total_base_cost > 0 
                          ? ((reseller.total_margin / reseller.total_base_cost) * 100).toFixed(2)
                          : 0;
                        
                        return (
                          <TableRow key={reseller.reseller?.id} data-testid={`reseller-revenue-${reseller.reseller?.id}`}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{reseller.reseller?.company_name}</p>
                                <p className="text-sm text-slate-500">{reseller.reseller?.email}</p>
                              </div>
                            </TableCell>
                            <TableCell>{reseller.order_count}</TableCell>
                            <TableCell className="mono text-green-600 font-semibold">
                              {fmt(reseller.total_sales)}
                            </TableCell>
                            <TableCell className="mono text-slate-600">
                              {fmt(reseller.total_base_cost)}
                            </TableCell>
                            <TableCell className="mono text-blue-600 font-semibold">
                              {fmt(reseller.total_margin)}
                            </TableCell>
                            <TableCell>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {marginPercentage}%
                              </span>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* ── Monthly GST Report Preview ── */}
            {gstReport?.rows?.length > 0 && (
              <Card data-testid="gst-report-table">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Monthly GST Report</CardTitle>
                    <p className="text-sm text-slate-500 mt-1">GST @ {gstReport.gst_rate_pct}% — provisioned orders only</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => downloadCSV(gstReport.rows, gstReport.gst_rate_pct)} className="flex items-center gap-1.5" data-testid="re-download-csv-btn">
                    <Download className="h-3.5 w-3.5" /> CSV
                  </Button>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Month</TableHead>
                        <TableHead className="text-right">Orders</TableHead>
                        <TableHead className="text-right">Base Revenue</TableHead>
                        <TableHead className="text-right text-amber-700">GST ({gstReport.gst_rate_pct}%)</TableHead>
                        <TableHead className="text-right">Total (incl. GST)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {gstReport.rows.map((row) => (
                        <TableRow key={row.month_key} data-testid={`gst-row-${row.month_key}`}>
                          <TableCell className="font-medium">{row.month}</TableCell>
                          <TableCell className="text-right">{row.order_count}</TableCell>
                          <TableCell className="text-right mono">{fmt(row.base_revenue)}</TableCell>
                          <TableCell className="text-right mono text-amber-700 font-semibold">{fmt(row.gst_collected)}</TableCell>
                          <TableCell className="text-right mono font-semibold text-green-700">{fmt(row.total_revenue)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-slate-50 font-bold border-t-2">
                        <TableCell className="font-bold">TOTAL</TableCell>
                        <TableCell className="text-right font-bold">{gstReport.rows.reduce((s,r)=>s+r.order_count,0)}</TableCell>
                        <TableCell className="text-right mono font-bold">{fmt(gstReport.rows.reduce((s,r)=>s+r.base_revenue,0))}</TableCell>
                        <TableCell className="text-right mono font-bold text-amber-700">{fmt(gstReport.rows.reduce((s,r)=>s+r.gst_collected,0))}</TableCell>
                        <TableCell className="text-right mono font-bold text-green-700">{fmt(gstReport.rows.reduce((s,r)=>s+r.total_revenue,0))}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
